import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Zap, ShieldCheck, Sparkles, ArrowUpRight } from "lucide-react";
import { WebIcon } from "../components/Logo";
import { GrainOverlay } from "../components/GrainOverlay";

const IMG = {
  heroBg:
    "https://images.unsplash.com/photo-1709377059113-3c429b2e0796?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBnZW9tZXRyaWMlMjB3ZWJ8ZW58MHx8fHwxNzg1MDI5NTIyfDA&ixlib=rb-4.1.0&q=85",
  workspace:
    "https://images.unsplash.com/photo-1510519138101-570d1dca3d66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwY3JlYXRpdmUlMjBhZ2VuY3klMjB3b3Jrc3BhY2UlMjBkYXJrfGVufDB8fHx8MTc4NTAyOTUyMnww&ixlib=rb-4.1.0&q=85",
  dashboard:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwZGlnaXRhbCUyMGRhc2hib2FyZCUyMG1vY2t1cCUyMGRhcmt8ZW58MHx8fHwxNzg1MDI5NTIzfDA&ixlib=rb-4.1.0&q=85",
};

const stats = [
  { value: "72hr", label: "Launch time" },
  { value: "100%", label: "Custom-built" },
  { value: "24/7", label: "Care & support" },
];

const EASE = [0.76, 0, 0.24, 1];

// Masked line-by-line reveal
const Line = ({ children, delay = 0, reduced }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={reduced ? { y: 0 } : { y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Home() {
  const heroRef = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 140]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.65], [1, 0]);
  const iconRotate = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <div data-testid="home-page" className="bg-charcoal">
      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        data-testid="hero"
        className="relative min-h-screen overflow-hidden bg-charcoal"
      >
        {/* Parallax background */}
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0" aria-hidden="true">
          <img src={IMG.heroBg} alt="" className="w-full h-full object-cover opacity-[0.28]" />
          <div className="absolute inset-0 bg-charcoal/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/50 to-charcoal/30" />
        </motion.div>

        <GrainOverlay opacity={0.16} />

        {/* Rotating decorative web mark */}
        <motion.div style={{ rotate: iconRotate }} className="absolute -right-32 top-10 z-0 hidden lg:block" aria-hidden="true">
          <WebIcon className="w-[560px] h-[560px] opacity-[0.06]" stroke="#87CEEB" />
        </motion.div>

        {/* Content */}
        <motion.div
          style={{ y: contentY, opacity: contentOpacity }}
          className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-center pt-32 pb-56"
        >
          <motion.div
            initial={reduced ? { opacity: 1 } : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <span className="inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.25em] text-sky-brand border border-sky-brand/30 rounded-full px-4 py-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sky-brand animate-pulse" />
              Local Web Design Agency
            </span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tighter leading-[0.9] text-white mt-8 text-6xl sm:text-7xl lg:text-[7.5vw]">
            <Line delay={0.05} reduced={reduced}>We Elevate</Line>
            <Line delay={0.16} reduced={reduced}>
              <span className="font-serif italic font-normal text-sky-brand pr-3">Your</span>Business
            </Line>
            <Line delay={0.27} reduced={reduced}>Online.</Line>
          </h1>

          <motion.p
            initial={reduced ? { opacity: 1 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.5 }}
            className="font-body text-lg md:text-xl text-white/70 mt-10 max-w-xl leading-relaxed"
          >
            Beautiful, high-performing websites for small businesses — luxury-agency polish
            with genuine local accessibility.
          </motion.p>

          <motion.div
            initial={reduced ? { opacity: 1 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.62 }}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-button"
              className="font-display group bg-sky-brand text-charcoal hover:brightness-105 active:scale-[0.98] transition-[transform,filter] duration-300 font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center gap-2"
            >
              Get Your Free Website Preview
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-button"
              className="font-display bg-white/5 backdrop-blur-xl border border-white/15 text-white hover:border-sky-brand hover:text-sky-brand active:scale-[0.98] transition-[transform,color,border-color] duration-300 font-semibold px-8 py-4 rounded-full inline-flex items-center justify-center"
            >
              View Services
            </Link>
          </motion.div>
        </motion.div>

        {/* Floating glass stat strip */}
        <motion.div
          initial={reduced ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
          className="absolute bottom-0 left-0 right-0 z-20"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="backdrop-blur-xl bg-white/[0.04] border-t border-x border-white/10 rounded-t-[2rem] grid grid-cols-3 divide-x divide-white/10">
              {stats.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label}`} className="px-4 sm:px-8 py-7 sm:py-9">
                  <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-sky-brand tracking-tight">{s.value}</div>
                  <div className="font-body text-xs sm:text-sm text-white/50 mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* ============ WHY ELEVATED — Bento ============ */}
      <section data-testid="why-elevated" className="relative bg-charcoal py-24 md:py-36 overflow-hidden">
        <GrainOverlay opacity={0.12} />
        <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: EASE }}
            className="max-w-2xl"
          >
            <span className="font-display text-sm uppercase tracking-[0.2em] font-semibold text-sky-brand">Why Elevated Web</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-5 leading-[1.05]">
              Everything your business needs to <span className="font-serif italic font-normal text-sky-brand">shine</span> online.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-16 auto-rows-[minmax(220px,auto)]">
            <BentoCard
              testid="perk-card-0"
              className="md:col-span-8 md:row-span-2"
              image={IMG.dashboard}
              icon={Zap}
              title="Fast, Modern Sites"
              text="Lightning-quick pages engineered to impress and convert — built on modern tooling, optimized to the last millisecond."
              large
              delay={0}
            />
            <BentoCard
              testid="perk-card-1"
              className="md:col-span-4"
              icon={ShieldCheck}
              title="Trusted & Reliable"
              text="Ongoing care and support so your site never falls behind."
              delay={0.1}
            />
            <BentoCard
              testid="perk-card-2"
              className="md:col-span-4"
              image={IMG.workspace}
              icon={Sparkles}
              title="Premium Design"
              text="Luxury-grade design, made accessible for local businesses."
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section className="relative bg-charcoal py-28 md:py-40 overflow-hidden border-t border-white/10">
        <GrainOverlay opacity={0.12} />
        <div className="relative z-20 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-display text-5xl md:text-7xl font-bold tracking-tighter text-white leading-[0.95]"
          >
            Ready to <span className="font-serif italic font-normal text-sky-brand">elevate</span> your presence?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
            className="font-body text-white/60 mt-8 max-w-xl mx-auto text-lg"
          >
            Tell us about your business and we'll send you a free website preview — no strings attached.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
            className="mt-12"
          >
            <Link
              to="/contact"
              data-testid="strip-cta-button"
              className="font-display group bg-sky-brand text-charcoal hover:brightness-105 active:scale-[0.98] transition-[transform,filter] duration-300 font-semibold px-10 py-5 rounded-full inline-flex items-center gap-2 text-lg"
            >
              Get Started
              <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

const BentoCard = ({ testid, className = "", image, icon: Icon, title, text, large = false, delay = 0 }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#222222] p-8 md:p-10 flex flex-col justify-end min-h-full hover:border-sky-brand/40 transition-colors duration-500 ${className}`}
  >
    {image && (
      <>
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 group-hover:scale-105 transition-[opacity,transform] duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#181818] via-[#181818]/70 to-transparent" />
      </>
    )}
    <div className="relative z-10">
      <div className="w-14 h-14 rounded-2xl bg-sky-brand/15 backdrop-blur-md border border-sky-brand/20 flex items-center justify-center">
        <Icon className="text-sky-brand" size={26} />
      </div>
      <h3 className={`font-display font-semibold text-white mt-6 ${large ? "text-3xl md:text-4xl" : "text-2xl"}`}>{title}</h3>
      <p className={`font-body text-white/60 mt-3 leading-relaxed ${large ? "max-w-md text-lg" : ""}`}>{text}</p>
    </div>
  </motion.div>
);
